import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Link,
  Autocomplete,
  Paper
} from '@mui/material';
import { OpenInNew } from '@mui/icons-material';
import axios from 'axios';
import { formatPartMasterOptionLabel } from '../../../utils/partMasterLabel';

const BasicFieldsSection = ({
  formData,
  handleChange,
  handlePartMasterSelect,
  selectedComponent,
  assemblies,
  dropdownOptions,
  isOriginalEquipment,
  handleNavigateToAddAssembly,
  handleNavigateToMasterData,
  handleNavigateToAddPart,
  readOnly = false
}) => {
  // State for Part Master search
  const [partsOptions, setPartsOptions] = useState(dropdownOptions.parts || []);
  const [partsLoading, setPartsLoading] = useState(false);
  const [partsSearchInput, setPartsSearchInput] = useState('');
  const searchTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Initialize parts options from dropdownOptions
  useEffect(() => {
    if (dropdownOptions.parts && dropdownOptions.parts.length > 0) {
      setPartsOptions(dropdownOptions.parts);
    }
  }, [dropdownOptions.parts]);

  // Ensure selected part is in options
  useEffect(() => {
    if (formData.partId) {
      const partId = formData.partId?.toString() || formData.partId;
      const isPartInOptions = partsOptions.some(part => {
        const optionId = part._id?.toString() || part._id;
        return optionId === partId;
      });

      // If selected part is not in options, fetch it
      if (!isPartInOptions && !partsLoading) {
        axios.get(`/api/parts/${partId}`)
          .then(response => {
            if (response.data) {
              setPartsOptions(prev => {
                // Check if it's already there to avoid duplicates
                const exists = prev.some(p => {
                  const pId = p._id?.toString() || p._id;
                  return pId === partId;
                });
                return exists ? prev : [...prev, response.data];
              });
            }
          })
          .catch(error => {
            console.error('Error fetching selected part:', error);
          });
      }
    }
  }, [formData.partId, partsOptions, partsLoading]);

  // Fetch parts with search (optimized with request cancellation)
  const fetchParts = useCallback(async (searchTerm = '') => {
    // Cancel previous request if it exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Create new AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    
    setPartsLoading(true);
    try {
      const params = {
        isActive: 'true',
        pageSize: 50, // Reduced from 100 - sufficient for dropdown
        page: 1
      };
      if (searchTerm && searchTerm.trim()) {
        params.search = searchTerm.trim();
      }
      const response = await axios.get('/api/parts', { 
        params,
        signal: abortController.signal // Cancel previous request if new one is made
      });
      
      // Only update if request wasn't aborted
      if (!abortController.signal.aborted) {
        const fetchedParts = response.data.parts || response.data || [];
        setPartsOptions(fetchedParts);
      }
    } catch (error) {
      // Ignore abort errors (expected when canceling previous requests)
      if (error.name !== 'CanceledError' && error.name !== 'AbortError' && error.code !== 'ERR_CANCELED') {
        console.error('Error fetching parts:', error);
        setPartsOptions([]);
      }
    } finally {
      // Only update loading state if this is still the current request
      if (!abortController.signal.aborted) {
        setPartsLoading(false);
      }
    }
  }, []);

  // Debounced search handler
  const handlePartsInputChange = useCallback((event, newInputValue, reason) => {
    setPartsSearchInput(newInputValue);
    
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // If user is typing (not selecting), debounce the search
    if (reason === 'input') {
      const trimmedValue = newInputValue.trim();
      
      // Only search if:
      // 1. User has typed at least 2 characters, OR
      // 2. Input is empty (show initial results)
      if (trimmedValue.length === 0 || trimmedValue.length >= 2) {
        searchTimeoutRef.current = setTimeout(() => {
          fetchParts(trimmedValue);
        }, 500); // Increased to 500ms debounce for better efficiency
      } else if (trimmedValue.length === 1) {
        // For single character, show initial list (no search)
        fetchParts('');
      }
    } else if (reason === 'clear') {
      // Reset to initial parts list when cleared
      fetchParts('');
    }
  }, [fetchParts]);

  // Cleanup timeout and abort controller on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);
  return (
    <>
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          label="Component Code"
          value={formData.code || ''}
          onChange={handleChange('code')}
          required
          disabled={!!selectedComponent || readOnly}
          sx={{ maxWidth: '250px' }}
          helperText={selectedComponent 
            ? "Format: {ASSEMBLY_TYPE}-{POSITION}-{SEQUENCE} (e.g., ENG-LH-001)"
            : "Auto-generated from Assembly and Position when both are selected. You can manually edit this field if needed."
          }
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FormControl fullWidth required sx={{ minWidth: 200 }}>
            <InputLabel 
              sx={{ 
                color: '#212121',
                fontSize: '14px',
                '&.Mui-focused': {
                  color: '#1976d2'
                },
                '&.MuiInputLabel-shrink': {
                  color: '#1976d2'
                }
              }}
            >
              Assembly
            </InputLabel>
            <Select
              value={formData.assemblyId || ''}
              label="Assembly"
              onChange={handleChange('assemblyId')}
              disabled={!!selectedComponent || readOnly}
              sx={{
                minWidth: 200,
                '& .MuiSelect-select': {
                  color: '#212121',
                  fontSize: '14px',
                  fontWeight: 400
                },
                '&.Mui-disabled .MuiSelect-select': {
                  color: 'rgba(0, 0, 0, 0.38)'
                }
              }}
              MenuProps={{
                autoWidth: false,
                PaperProps: {
                  sx: {
                    maxHeight: 400,
                    minWidth: 700,
                    width: 'auto',
                    backgroundColor: '#ffffff',
                    '& .MuiMenuItem-root': {
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      padding: '12px 16px',
                      minHeight: 'auto',
                      lineHeight: 1.5,
                      overflow: 'visible',
                      textOverflow: 'unset',
                      color: '#212121',
                      fontSize: '14px',
                      fontWeight: 400,
                      backgroundColor: '#ffffff',
                      '&:hover': {
                        backgroundColor: '#f5f5f5',
                        color: '#212121'
                      },
                      '&.Mui-selected': {
                        backgroundColor: '#e3f2fd',
                        color: '#1976d2',
                        '&:hover': {
                          backgroundColor: '#bbdefb'
                        }
                      }
                    }
                  }
                },
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left',
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: 'left',
                },
              }}
            >
              {assemblies.map((assembly) => {
                const assemblyId = assembly._id?.toString() || assembly._id;
                return (
                  <MenuItem key={assemblyId} value={assemblyId}>
                    {assembly.type?.name || assembly.typeName || 'Unknown'} - SN: {assembly.serialNumber || 'N/A'} - Pos: {assembly.position || 'N/A'}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          {!selectedComponent && !readOnly && (
            <Link
              component="button"
              type="button"
              onClick={handleNavigateToAddAssembly}
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 0.5,
                fontSize: '0.875rem',
                textDecoration: 'none',
                whiteSpace: 'nowrap',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              Add Assembly
              <OpenInNew sx={{ fontSize: '0.875rem' }} />
            </Link>
          )}
        </Box>
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControlLabel
          control={
            <Checkbox
              checked={isOriginalEquipment}
              onChange={handleChange('isOriginalEquipment')}
              disabled={!!selectedComponent || readOnly}
            />
          }
          label="Original Equipment"
        />
        <Typography variant="caption" display="block" sx={{ mt: 0.5, color: 'text.secondary' }}>
          Check if this component came with the original assembly. This will auto-fill installation details from the selected assembly.
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Autocomplete
            fullWidth
            required
            disabled={readOnly}
            options={dropdownOptions.ataChapters || []}
            getOptionLabel={(option) => `${option.code} - ${option.name}`}
            isOptionEqualToValue={(option, value) => {
              const optionId = option._id?.toString() || option._id;
              const valueId = value._id?.toString() || value._id;
              return optionId === valueId;
            }}
            value={dropdownOptions.ataChapters.find(ata => {
              const ataId = ata._id?.toString() || ata._id;
              return ataId === (formData.ataChapterId?.toString() || formData.ataChapterId);
            }) || null}
            onChange={readOnly ? undefined : (event, newValue) => {
              const ataId = newValue ? (newValue._id?.toString() || newValue._id) : '';
              handleChange('ataChapterId')({ target: { value: ataId } });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="ATA Chapter"
                required
                sx={{ minWidth: 200 }}
              />
            )}
            sx={{ minWidth: 200 }}
            ListboxProps={{
              style: {
                maxHeight: 400
              }
            }}
            PaperComponent={({ children, ...other }) => (
              <Paper
                {...other}
                sx={{
                  maxHeight: 400,
                  minWidth: 700,
                  width: 'auto',
                  backgroundColor: '#ffffff',
                  '& .MuiAutocomplete-option': {
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    padding: '12px 16px',
                    minHeight: 'auto',
                    lineHeight: 1.5,
                    overflow: 'visible',
                    textOverflow: 'unset',
                    color: '#212121',
                    fontSize: '14px',
                    fontWeight: 400,
                    backgroundColor: '#ffffff',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                      color: '#212121'
                    },
                    '&.Mui-focused': {
                      backgroundColor: '#e3f2fd',
                      color: '#1976d2'
                    },
                    '&[aria-selected="true"]': {
                      backgroundColor: '#e3f2fd',
                      color: '#1976d2',
                      '&:hover': {
                        backgroundColor: '#bbdefb'
                      }
                    }
                  }
                }}
              >
                {children}
              </Paper>
            )}
          />
          {!readOnly && (
            <Link
              component="button"
              type="button"
              onClick={() => handleNavigateToMasterData('ata-chapters', 0)}
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 0.5,
                fontSize: '0.875rem',
                textDecoration: 'none',
                whiteSpace: 'nowrap',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              Add ATA Chapter
              <OpenInNew sx={{ fontSize: '0.875rem' }} />
            </Link>
          )}
        </Box>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Autocomplete
            fullWidth
            required
            options={partsOptions}
            loading={partsLoading}
            disabled={readOnly}
            getOptionLabel={(option) => formatPartMasterOptionLabel(option)}
            isOptionEqualToValue={(option, value) => {
              const optionId = option._id?.toString() || option._id;
              const valueId = value._id?.toString() || value._id;
              return optionId === valueId;
            }}
            value={partsOptions.find(part => {
              const partId = part._id?.toString() || part._id;
              return partId === (formData.partId?.toString() || formData.partId);
            }) || null}
            onChange={readOnly ? undefined : (event, newValue) => {
              if (handlePartMasterSelect) {
                handlePartMasterSelect(newValue || null);
              } else {
                const partId = newValue ? (newValue._id?.toString() || newValue._id) : '';
                handleChange('partId')({ target: { value: partId } });
              }
            }}
            onInputChange={readOnly ? undefined : handlePartsInputChange}
            inputValue={partsSearchInput}
            filterOptions={(options) => options} // Disable client-side filtering since we're doing server-side search
            renderInput={(params) => (
              <TextField
                {...params}
                label="Part Master"
                required
                sx={{ minWidth: 200 }}
              />
            )}
            sx={{ minWidth: 200 }}
            ListboxProps={{
              style: {
                maxHeight: 400
              }
            }}
            PaperComponent={({ children, ...other }) => (
              <Paper
                {...other}
                sx={{
                  maxHeight: 400,
                  minWidth: 700,
                  width: 'auto',
                  backgroundColor: '#ffffff',
                  '& .MuiAutocomplete-option': {
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    padding: '12px 16px',
                    minHeight: 'auto',
                    lineHeight: 1.5,
                    overflow: 'visible',
                    textOverflow: 'unset',
                    color: '#212121',
                    fontSize: '14px',
                    fontWeight: 400,
                    backgroundColor: '#ffffff',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                      color: '#212121'
                    },
                    '&.Mui-focused': {
                      backgroundColor: '#e3f2fd',
                      color: '#1976d2'
                    },
                    '&[aria-selected="true"]': {
                      backgroundColor: '#e3f2fd',
                      color: '#1976d2',
                      '&:hover': {
                        backgroundColor: '#bbdefb'
                      }
                    }
                  }
                }}
              >
                {children}
              </Paper>
            )}
          />
          {!readOnly && (
            <Link
              component="button"
              type="button"
              onClick={handleNavigateToAddPart}
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 0.5,
                fontSize: '0.875rem',
                textDecoration: 'none',
                whiteSpace: 'nowrap',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              Add Part
              <OpenInNew sx={{ fontSize: '0.875rem' }} />
            </Link>
          )}
        </Box>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Part Number"
          value={formData.partNumber || ''}
          onChange={handleChange('partNumber')}
          required
          disabled={readOnly}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Part Name"
          value={formData.partName || ''}
          disabled
          InputProps={{ readOnly: true }}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Description"
          value={formData.description || ''}
          onChange={handleChange('description')}
          required
          multiline
          rows={2}
          disabled={readOnly}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Serial Number"
          value={formData.serialNumber || ''}
          onChange={handleChange('serialNumber')}
          disabled={readOnly}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Position"
          value={formData.position || ''}
          onChange={handleChange('position')}
          required
          placeholder="e.g., LH, RH"
          disabled={readOnly}
        />
      </Grid>
    </>
  );
};

export default BasicFieldsSection;
